// Browser fetch client for the Candidate BFF API — used by the frontend and Kubb-generated clients.
// Shape matches Kubb’s ResponseConfig so `return res.data` in generated code is type-safe.
//
// @see https://kubb.dev/kubb/guide/fetch
let BASE_URL =
  (typeof import.meta === 'undefined'
    ? undefined
    : (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
        ?.VITE_API_URL) ?? 'http://localhost:3001';

/** Optional — set from Keycloak (or any OIDC) so every BFF request sends Bearer. */
let resolveAccessToken: (() => Promise<string | undefined>) | null = null;

export function setAccessTokenProvider(fn: (() => Promise<string | undefined>) | null) {
  resolveAccessToken = fn;
}

let handleUnauthorized: (() => void) | null = null;
let unauthorizedInFlight = false;

export function setUnauthorizedHandler(fn: (() => void) | null) {
  handleUnauthorized = fn;
  if (!fn) {
    unauthorizedInFlight = false;
  }
}

function notifyUnauthorized(): void {
  if (unauthorizedInFlight || !handleUnauthorized) return;
  unauthorizedInFlight = true;
  try {
    handleUnauthorized();
  } catch (err) {
    unauthorizedInFlight = false;
    console.error('[candidate-api-sdk] unauthorized handler threw', err);
  }
}

/**
 * Força um refresh do token e devolve o novo bearer — registrado pelo
 * KeycloakGate (`keycloak.updateToken(-1)`). Usado SÓ na recuperação de 401
 * (EU-665): quando a 1ª tentativa sai com um bearer vencido/ausente (o provider
 * devolveu `undefined` num blip do `updateToken`, ou o access token venceu
 * enquanto um formulário longo ficava aberto), o `fetchClient` força o refresh e
 * re-tenta uma vez antes de tratar a sessão como morta. `null` quando não há
 * refresher (deslogado); `undefined` do retorno = o refresh não recuperou token.
 */
let forceTokenRefresh: (() => Promise<string | undefined>) | null = null;

export function setForceTokenRefresh(fn: (() => Promise<string | undefined>) | null) {
  forceTokenRefresh = fn;
}

/**
 * Observador de atividade de rede do transporte — EU-711.
 *
 * `null` por default: quem NÃO registra observador não muda de comportamento em
 * nada (o app candidato registra; os outros consumidores do package não). Existe
 * porque o gatilho de "sem internet" tem que valer para TODO o App Candidato, e
 * este é o único ponto por onde passam todas as superfícies (listagem, navegação,
 * perfil, conclusão de etapa, upload de currículo).
 *
 * Emite três eventos por request, com um id de correlação:
 * - `start`         — a request saiu (arma o relógio de requisição pendurada)
 * - `success`       — respondeu (qualquer status HTTP, inclusive 4xx/5xx): a
 *                     conexão ESTÁ viva, então isto CANCELA suspeita de offline
 * - `network-error` — `fetch` rejeitou (queda de conexão / abort de timeout),
 *                     o mesmo caso que o transporte reporta como `status: 0`
 * - `settled`       — terminou sem responder e SEM ser falha de conexão (o
 *                     provider de token lançou, por exemplo). Não diz nada sobre
 *                     a conexão; existe para o observador SOLTAR a request. Sem
 *                     ele a request ficava "em voo" para sempre e o relógio de
 *                     requisição pendurada abria a tela de offline mais tarde,
 *                     com a conexão perfeita
 *
 * `success` em 4xx/5xx é intencional e load-bearing: erro de servidor com
 * internet ativa NÃO é falta de conexão (o candidato tem que ver a tela de erro
 * de servidor, nunca "Você está sem internet").
 */
export type NetworkActivityEvent = 'start' | 'success' | 'network-error' | 'settled';

export interface NetworkActivityMeta {
  /**
   * A request é elegível ao relógio de "pendurada"? `false` para quem declarou
   * um teto de tempo PRÓPRIO (`timeoutMs`) — o upload de currículo tem 300 s
   * (EU-558/EU-696) porque um PDF de 30 MB em rede móvel demora de propósito.
   * Uma request que anunciou que demora NÃO é evidência de falta de conexão:
   * sem esta distinção, um upload saudável de 40 s abriria "Você está sem
   * internet" no meio do envio.
   */
  watchdog: boolean;
}

export type NetworkActivityObserver = (
  event: NetworkActivityEvent,
  requestId: number,
  meta: NetworkActivityMeta
) => void;

let networkActivityObserver: NetworkActivityObserver | null = null;
let networkActivitySeq = 0;

export function setNetworkActivityObserver(fn: NetworkActivityObserver | null) {
  networkActivityObserver = fn;
}

/** Notifica o observador sem NUNCA deixar um erro dele derrubar a request. */
function notifyNetworkActivity(
  event: NetworkActivityEvent,
  requestId: number,
  meta: NetworkActivityMeta
): void {
  if (!networkActivityObserver) return;
  try {
    networkActivityObserver(event, requestId, meta);
  } catch (err) {
    console.error('[candidate-api-sdk] network activity observer threw', err);
  }
}

export function setBaseUrl(url: string) {
  BASE_URL = url.replace(/\/+$/, '');
}

/** Current BFF base URL — for raw fetches that bypass the generated client
 *  (e.g. the authenticated resume proxy `GET /me/resume/file`). */
export function getApiBaseUrl(): string {
  return BASE_URL;
}

/** Resolve the current bearer via the registered provider (Keycloak/OIDC).
 *  Used by raw fetches that need the same Authorization header the generated
 *  client attaches. `undefined` when no provider is set (anonymous). */
export async function resolveBearer(): Promise<string | undefined> {
  return resolveAccessToken ? await resolveAccessToken() : undefined;
}

export interface RequestConfig<TBody = unknown> {
  method: string;
  url: string;
  params?: Record<string, unknown>;
  data?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Keep the request alive past page unload (e.g. fire-and-forget analytics
   * posted right before a `window.location` navigation). Maps to the native
   * `fetch` `keepalive` option; bodies are capped at 64KB by the browser.
   */
  keepalive?: boolean;
  /**
   * Teto de tempo DESTA request, em ms — sobrepõe o default global
   * (`VITE_API_REQUEST_TIMEOUT_MS`, 30 s). `null` desliga o teto.
   *
   * Existe por causa do upload de currículo (EU-558): o multipart de até 30 MB
   * não cabe em 30 s num upstream móvel, a request era abortada no meio e a
   * tela mostrava erro genérico sem nada persistido. Requests de leitura
   * continuam no default — o teto curto é proteção contra pendurar a UI.
   */
  timeoutMs?: number | null;
}

/**
 * Resolve o teto de tempo efetivo de uma request: o override explícito quando
 * vier (número positivo finito), `null` para desligar, ou o default do
 * ambiente. Exportada para teste — é o ponto onde o bug do upload nasceu.
 */
export function resolveRequestTimeoutMs(override?: number | null): number | null {
  if (override === null) return null;
  if (override !== undefined && Number.isFinite(override) && override > 0) return override;

  const meta =
    typeof import.meta !== 'undefined' && import.meta.env && typeof import.meta.env === 'object'
      ? (import.meta.env as Record<string, string | undefined>)
      : {};
  const raw = meta.VITE_API_REQUEST_TIMEOUT_MS;
  const timeoutMs = raw != null && String(raw) !== '' ? Number(raw) : 30_000;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return null;
  return timeoutMs;
}

/** Used in generated client generics for OpenAPI error bodies (runtime errors are thrown). */
export type ResponseErrorConfig<T> = {
  error: T;
  status: number;
};

/** Kubb-compatible success envelope — same as @kubb/plugin-client fetch guide. */
export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
};

export type Client = <TData, _TError = unknown, TBody = unknown>(
  config: RequestConfig<TBody>
) => Promise<ResponseConfig<TData>>;

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(path, BASE_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null) continue;
          url.searchParams.append(k, String(item));
        }
        return;
      }
      url.searchParams.append(k, String(v));
    });
  }
  return url.toString();
}

function resolveFetchSignal(
  userSignal: AbortSignal | undefined,
  timeoutOverride?: number | null
): AbortSignal | undefined {
  const timeoutMs = resolveRequestTimeoutMs(timeoutOverride);
  if (timeoutMs === null) {
    return userSignal;
  }
  const TimeoutSignal =
    typeof AbortSignal !== 'undefined'
      ? (AbortSignal as typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }).timeout
      : undefined;
  if (typeof TimeoutSignal !== 'function') {
    return userSignal;
  }
  const timeoutSignal = TimeoutSignal(timeoutMs);
  if (!userSignal) {
    return timeoutSignal;
  }
  const anyFn = (
    AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal }
  ).any;
  if (typeof anyFn === 'function') {
    return anyFn([userSignal, timeoutSignal]);
  }
  return userSignal;
}

export async function fetchClient<TData, _TError = unknown, TBody = unknown>(
  config: RequestConfig<TBody>
): Promise<ResponseConfig<TData>> {
  const {
    method,
    url,
    params,
    data,
    headers: customHeaders,
    signal,
    keepalive,
    timeoutMs,
  } = config;
  const isBodyMethod = !['GET', 'HEAD'].includes(method.toUpperCase());
  const isFormData = data instanceof FormData;

  // Only set the JSON content-type when we actually have a body — Fastify
  // rejects empty `application/json` payloads with FST_ERR_CTP_EMPTY_JSON_BODY.
  // FormData is handled separately (the browser sets multipart automatically).
  const hasJsonBody = isBodyMethod && data != null && !isFormData;
  const baseHeaders: Record<string, string> = {
    ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
    ...(customHeaders ?? {}),
  };

  // Uma tentativa da request com um bearer específico. Extraído para permitir a
  // re-tentativa na recuperação de 401 (EU-665) sem duplicar o wrapping do erro
  // de rede (`status: 0`). Cada tentativa recebe seu próprio signal de timeout.
  const sendRequest = async (bearer: string | undefined): Promise<Response> => {
    const headers: Record<string, string> = { ...baseHeaders };
    if (bearer) {
      headers.authorization = `Bearer ${bearer}`;
    }
    try {
      return await fetch(buildUrl(url, params), {
        method,
        headers,
        ...(isBodyMethod && data != null
          ? { body: isFormData ? (data as FormData) : JSON.stringify(data) }
          : {}),
        ...(keepalive ? { keepalive: true } : {}),
        signal: resolveFetchSignal(signal, timeoutMs),
      });
    } catch (err) {
      const wrapped = new Error((err as Error).message ?? 'Network error') as Error & {
        status: number;
        body: unknown;
      };
      wrapped.status = 0;
      wrapped.body = { message: (err as Error).message };
      throw wrapped;
    }
  };

  // EU-711 — a request inteira (incluindo a re-tentativa de 401) conta como UMA
  // unidade de atividade de rede: o relógio de "pendurada" mede o que o
  // candidato espera, não cada tentativa interna.
  const requestId = ++networkActivitySeq;
  const activityMeta: NetworkActivityMeta = { watchdog: timeoutMs === undefined };
  notifyNetworkActivity('start', requestId, activityMeta);

  let res: Response;
  try {
    const token = resolveAccessToken ? await resolveAccessToken() : undefined;
    res = await sendRequest(token);

    // Recuperação de 401 (EU-665): a 1ª tentativa pode sair com um bearer
    // vencido/ausente (blip do `updateToken`, ou access token que venceu
    // enquanto um formulário longo ficava aberto) → o BFF responde 401 em ~1ms.
    // Antes de tratar a sessão como morta (limpar token + redirect via
    // `notifyUnauthorized`), força um refresh e re-tenta UMA vez com o bearer
    // novo. Só quando a re-tentativa também 401 (ou não há token novo) a sessão
    // é dada como perdida — evitando expulsar o candidato e perder o que ele
    // preencheu por um 401 transitório.
    if (res.status === 401 && forceTokenRefresh) {
      const refreshed = await forceTokenRefresh();
      if (refreshed) {
        res = await sendRequest(refreshed);
      }
    }
  } catch (err) {
    // Só `status: 0` é falta de conexão. Um erro de outra natureza (o provider
    // de token lançando, por exemplo) não é sinal de rede e NÃO pode alimentar
    // o detector de offline — senão o candidato veria "sem internet" por um
    // problema que não é de conexão. Mas TODO caminho de saída emite um evento
    // terminal: sem isso a request ficava presa "em voo" no observador.
    const isNetwork = (err as { status?: number } | undefined)?.status === 0;
    notifyNetworkActivity(isNetwork ? 'network-error' : 'settled', requestId, activityMeta);
    throw err;
  }

  // Respondeu — a conexão está viva. Vale para QUALQUER status: um 500 com
  // internet ativa cancela suspeita de offline em vez de confirmá-la.
  notifyNetworkActivity('success', requestId, activityMeta);

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401) {
      notifyUnauthorized();
    }
    const err = new Error('Request failed') as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = payload;
    throw err;
  }

  return {
    data: payload as TData,
    status: res.status,
    statusText: res.statusText,
  };
}

export default fetchClient;
